import { evaluate } from "@kyan/ui";
import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { tokenAddresses, tokens } from "#/config/web3/tokens";
import { useDepositFeatureFlag } from "#/hooks/featureFlags/useDepositFeatureFlag";
import { useChainId } from "#/hooks/useChainId";
import { useUsdcBalance } from "#/hooks/useUsdcBalance";
import { BungeeToken } from "#/services/bridge/bridge.types";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useIsEquityModalEditing } from "#/store/app/equityModal/equityModalEditState";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useShowBridgeHistory } from "#/store/app/equityModal/showBridgeHistory";
import { useAccount } from "#/store/wallet/account/account";
import { useAccountState } from "#/store/wallet/account/accountState";

import { useBridge } from "./hooks/useBridge";
import { useDeposit } from "./hooks/useDeposit";

export const useDepositFormController = () => {
  const { chainId } = useChainId();
  const {
    usdcBalance,
    refetch: refetchBalance,
    isLoading: isBalanceLoading,
  } = useUsdcBalance();
  const { equityModalPortfolio, setEquityModalPortfolio } =
    useEquityModalPortfolio();
  const { portfolioState } = useAccountState(equityModalPortfolio);
  const { setIsEquityModalEditing } = useIsEquityModalEditing();
  const { account, setAccount } = useAccount();
  const { isDevMode } = useIsDevMode();

  const usdcToken: BungeeToken = {
    ...tokens.USDC,
    chainId,
    address: tokenAddresses.USDC[chainId],
  };

  const [inputToken, setInputToken] = useState<BungeeToken>({ ...usdcToken });
  const [targetAccount, setTargetAccount] = useState(account);
  const [amount, setAmount] = useState("");
  const { showBridgeHistory } = useShowBridgeHistory();
  const [debouncedAmount] = useDebounceValue(amount, 600);
  const outputToken = usdcToken;

  const {
    ctaLabel: bridgeLabel,
    errorMessage: bridgeErrorMessage,
    warningMessage: bridgeWarningMessage,
    isLoading: isBridging,
    isLoadingQuote,
    handleBridge,
    isFormValid: bridgeFormIsValid,
    isDisabled: isBridgeDisabled,
    max: bridgeMax,
    bridgeId,
    bridgeStatus,
    quote,
    outputAmount,
  } = useBridge({
    amount: debouncedAmount,
    setAmount,
    setInputToken,
    inputToken,
    outputToken,
  });

  const {
    handleDeposit,
    errorMessage: depositErrorMessage,
    ctaLabel: depositLabel,
    isLoading: isDepositing,
    isFormValid: depositFormIsValid,
    isDisabled: isDepositDisabled,
    max: depositMax,
    min: depositMin,
  } = useDeposit({
    amount: debouncedAmount,
    account: isDevMode ? targetAccount : account,
  });

  const {
    isEnabled: isDepositButtonEnabled,
    depositButtonTitle,
    depositButtonTooltip,
  } = useDepositFeatureFlag();

  const isBridgeNeeded =
    inputToken.chainId !== outputToken.chainId ||
    inputToken.address !== outputToken.address;

  const defaultCtaLabel = isBridgeNeeded ? bridgeLabel : depositLabel;
  const disabledCtaLabel = depositButtonTitle ?? defaultCtaLabel;
  const ctaLabel =
    evaluate({
      [disabledCtaLabel as string]: !isDepositButtonEnabled,
    }) ?? defaultCtaLabel;

  const isFormValid = isBridgeNeeded ? bridgeFormIsValid : depositFormIsValid;
  const isSubmitDisabled = isBridgeNeeded
    ? isBridgeDisabled
    : isDepositDisabled;
  const isDisabled =
    !isFormValid || !isDepositButtonEnabled || isSubmitDisabled;

  const maxValue = isBridgeNeeded ? bridgeMax : depositMax;
  const minValue = isBridgeNeeded ? undefined : depositMin;
  const errorMessage = isBridgeNeeded
    ? bridgeErrorMessage
    : depositErrorMessage;
  const warningMessage = isBridgeNeeded ? bridgeWarningMessage : undefined;

  const isUsdcSelected =
    inputToken.chainId === chainId &&
    inputToken.address === tokenAddresses.USDC[chainId];

  const isLoading = isBridgeNeeded ? isBridging : isDepositing;

  const finalAmount =
    portfolioState.equity +
    (isBridgeNeeded ? Number(outputAmount) : Number(amount));

  const handleSubmit = async () => {
    if (isBridgeNeeded) {
      handleBridge();
    } else {
      handleDeposit();
    }
  };

  const setDepositAmount = (depositAmount: string) => {
    setAmount(depositAmount);
    setIsEquityModalEditing(Boolean(depositAmount));
  };

  return {
    isBridgeNeeded,
    handleSubmit,
    ctaLabel,
    tooltip: depositButtonTooltip,
    isFormValid,
    isDisabled,
    isLoading,
    isLoadingQuote,
    usdcBalance,
    refetchBalance,
    isBalanceLoading,
    account,
    setAccount,
    amount,
    equityModalPortfolio,
    setEquityModalPortfolio,
    setDepositAmount,
    portfolioState,
    errorMessage,
    warningMessage,
    finalAmount,
    isDevMode,
    targetAccount,
    setTargetAccount,
    setAmount,
    setInputToken,
    inputToken: isUsdcSelected
      ? { ...inputToken, balance: usdcBalance.toString() }
      : inputToken,
    minValue,
    maxValue,
    bridgeId,
    bridgeStatus,
    quote,
    showBridgeHistory,
  };
};
